import React, { useState } from "react";
import { Filter, Search, ChevronDown, X, ArrowUpDown } from "lucide-react";

const FilterSection = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  countryFilter,
  onCountryChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  onClearFilters,
  countries = [],
}) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const hasActiveFilters = searchTerm || statusFilter !== "all" || countryFilter !== "all";

  return (
    <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
      {/* Mobile Filter Toggle */}
      <div className="flex justify-between items-center mb-4 lg:hidden">
        <h3 className="flex items-center text-base font-semibold text-gray-900">
          <Filter className="mr-2 w-4 h-4 text-blue-600" />
          Filters
        </h3>
        <button
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="flex items-center px-3 py-2 text-sm bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
        >
          <span>{showMobileFilters ? "Hide" : "Show"}</span>
          <ChevronDown
            className={`w-4 h-4 ml-2 transition-transform ${
              showMobileFilters ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Desktop Filter Header */}
      <div className="hidden justify-between items-center mb-6 lg:flex">
        <h3 className="flex items-center text-lg font-semibold text-gray-900">
          <Filter className="mr-2 w-5 h-5 text-blue-600" />
          Filters & Search
        </h3>
      </div>

      {/* Filter Controls */}
      <div
        className={`space-y-4 ${
          showMobileFilters ? "block" : "hidden lg:block"
        }`}
      >
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name, email, phone, or ID..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-4 top-1/2 text-gray-400 -translate-y-1/2 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Grid */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => onStatusChange(e.target.value)}
              className="w-full px-4 py-3.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 appearance-none cursor-pointer font-medium"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <ChevronDown className="absolute right-4 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Country Filter */}
          <div className="relative">
            <select
              value={countryFilter}
              onChange={(e) => onCountryChange(e.target.value)}
              className="w-full px-4 py-3.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 appearance-none cursor-pointer font-medium"
            >
              <option value="all">All Countries</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Sort By */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value)}
              className="w-full px-4 py-3.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 appearance-none cursor-pointer font-medium"
            >
              <option value="name">Sort by Name</option>
              <option value="email">Sort by Email</option>
              <option value="country">Sort by Country</option>
              <option value="status">Sort by Status</option>
              <option value="date">Sort by Date</option>
            </select>
            <ChevronDown className="absolute right-4 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Sort Order */}
          <button
            onClick={() => onSortOrderChange(sortOrder === "asc" ? "desc" : "asc")}
            className="flex items-center justify-center px-4 py-3.5 text-sm bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border border-gray-200 rounded-xl transition-all font-medium"
          >
            <ArrowUpDown className="mr-2 w-4 h-4" />
            {sortOrder === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <div className="flex justify-end mt-4">
          <button
            onClick={onClearFilters}
            className="px-4 py-2 text-sm font-medium text-red-600 rounded-lg transition-colors hover:text-red-700 hover:bg-red-50"
          >
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterSection;

